
import React, { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import ROUTER_CONSTANT, { ROUTER_PARAMS } from '../constants/RouterConstant';
import PAGE_CONSTANT from "../constants/PageConstants";
import Loader from '../component/common/Loader';
import { trackPageView } from '../utils/clarity';
import Footer from '../component/homepage/footer';
import { UserDetails } from '../api/UserAPI';
import DirectoryPage from '../container/DirectoryPage';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { getUserType } from '../utils/cachedUser';
import FeatureRoute from '../component/subscription/FeatureRoute';
import RouteTransition from './RouteTransition';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (!localStorage.getItem(STORAGE_KEYS.TOKEN)) {
        return <Navigate to={ROUTER_CONSTANT.ROOT.LOGIN} replace />;
    }
    return <>{children}</>;
};

// Same token check as ProtectedRoute, plus a client-side role gate for
// society-admin-only screens. Defense-in-depth only — the backend is the
// real authority — but it stops an authenticated resident/guard from
// rendering admin UI (and triggering its data fetches) just by typing
// the URL, which today happens with zero client-side check.
const AdminRoute: React.FC<{ children: React.ReactNode; allow?: string[] }> = ({ children, allow = ['SOCIETY_ADMIN'] }) => {
    if (!localStorage.getItem(STORAGE_KEYS.TOKEN)) {
        return <Navigate to={ROUTER_CONSTANT.ROOT.LOGIN} replace />;
    }
    const role = getUserType();
    // An empty role means the cached user object isn't populated yet (e.g. a
    // deep link arriving before HomePage's fetch completes) — don't block on
    // an unknown role, only on a role we positively know isn't allowed.
    if (role && !allow.includes(role)) {
        return <Navigate to={ROUTER_CONSTANT.ROOT.HOME} replace />;
    }
    return <>{children}</>;
};

// Lazy load all page components for better performance
// Every page chunk is also fetched in the background once the app is idle (see LocalRoutes), so
// opening a feature never waits on the network and shows the full-screen page loader.
const pagePreloaders: Array<() => Promise<unknown>> = [];
const lazyPage = <T extends React.ComponentType<any>>(load: () => Promise<{ default: T }>) => {
    pagePreloaders.push(load);
    return lazy(load);
};
const Login = lazyPage(() => import('../container/login'));
const HomePage = lazyPage(() => import("../container/HomePage"));
const Notification = lazyPage(() => import("../container/NotificationPage"));
const Profile = lazyPage(() => import("../container/Profile"));
const MemberPage = lazyPage(() => import("../container/MemberPage"));
const Statement = lazyPage(() => import("../container/profile/Statement"));
const AddAsset = lazyPage(() => import("../container/profile/AddAsset"));
const EditProfile = lazyPage(() => import("../container/profile/EditProfile"));
const Visitor = lazyPage(() => import("../container/VisitorPage"));
const Polling = lazyPage(() => import("../container/PollingPage"));
const NoticeBoard = lazyPage(() => import("../container/NoticeBoard"));
const Gallary = lazyPage(() => import("../container/Gallary"));
const Complaint = lazyPage(() => import("../container/ComplaintPage"));
const AllMenuPage = lazyPage(() => import("../container/AllMenuPage"));
const Function = lazyPage(() => import("../container/function/Function"));
const ChatPage = lazyPage(() => import("../container/ChatPage"));
const UserQR = lazyPage(() => import("../container/UserQRPage"));
const UserAddress = lazyPage(() => import("../container/AddressPage"));
const Election = lazyPage(() => import("../container/ElectionPage"));
const ElectionDetails = lazyPage(() => import("../container/ElectionDetailsPage"));
const SocietyStatement = lazyPage(() => import("../container/SocietyStatement"));
const FunctionDetail = lazyPage(() => import("../container/function/FunctionDetail"));
const SocietyDox = lazyPage(() => import("../container/SocietyDoxs"));
const SocietyDetail = lazyPage(() => import("../container/SocietyDetails"));
const AddFunction = lazyPage(() => import("../container/function/AddFunction"));
const AdminEmergencyNumber = lazyPage(() => import("../container/societyAdmin/EmergencyNumber"));
const SocietySettingsPage = lazyPage(() => import("../container/societyAdmin/SocietySettingsPage"));
const SubscriptionPage = lazyPage(() => import("../container/societyAdmin/SubscriptionPage"));
const Onboarding = lazyPage(() => import("../container/OnboardingPage"));
const MemberRequest = lazyPage(() => import("../container/societyAdmin/MemberRequestPage"));
const AnalyticsDashboard = lazyPage(() => import("../container/societyAdmin/AnalyticsDashboardPage"));
const AgentPage = lazyPage(() => import("../container/AgentPage"));
const AgentSubscriptionPage = lazyPage(() => import("../container/agent/AgentSubscriptionPage"));
const ManageGuard = lazyPage(() => import("../container/societyAdmin/ManageGaurd"));
const ApprovalPage = lazyPage(() => import("../container/ApprovalPage"));
const UserMaintenancePage = lazyPage(() => import("../container/maintenance/UserMaintenancePage"));
const AdminMaintenancePage = lazyPage(() => import("../container/maintenance/AdminMaintenancePage"));
const PaymentVerificationsPage = lazyPage(() => import("../container/maintenance/PaymentVerificationsPage"));
const StatementCheckerPage = lazyPage(() => import("../container/maintenance/StatementCheckerPage"));
const StatementUploadPage = lazyPage(() => import("../container/maintenance/StatementUploadPage"));
const StatementHistoryPage = lazyPage(() => import("../container/maintenance/StatementHistoryPage"));
const QRScannerPage = lazyPage(() => import("../container/QRScannerPage"));
const SosHistoryPage = lazyPage(() => import("../container/guard/SosHistoryPage"));
const VehiclesPage = lazyPage(() => import("../container/VehiclesPage"));
const FundPage = lazyPage(() => import("../container/FundPage"));
const DirectoryNeighboursPage = lazyPage(() => import('../container/directory/DirectoryNeighboursPage'));
const DirectoryVehiclesPage = lazyPage(() => import('../container/directory/DirectoryVehiclesPage'));
const DirectoryPetsPage = lazyPage(() => import('../container/directory/DirectoryPetsPage'));
const DirectoryGuardsPage = lazyPage(() => import('../container/directory/DirectoryGuardsPage'));
const CommitteePage = lazyPage(() => import('../container/directory/CommitteePage'));
const DailyHelpPage = lazyPage(() => import('../container/DailyHelpPage'));
const DailyHelpCategoryPage = lazyPage(() => import('../container/DailyHelpCategoryPage'));
const DailyHelpProfilePage = lazyPage(() => import('../container/DailyHelpProfilePage'));
const DailyHelpMyFlatsPage = lazyPage(() => import('../container/DailyHelpMyFlatsPage'));
const AmenitiesPage = lazyPage(() => import('../container/resident/AmenitiesPage'));
const AmenityDetailPage = lazyPage(() => import('../container/resident/AmenityDetailPage'));
const MyAmenityBookingsPage = lazyPage(() => import('../container/resident/MyAmenityBookingsPage'));
const AmenityBookingRequestsPage = lazyPage(() => import('../container/societyAdmin/AmenityBookingRequestsPage'));

// Loading fallback component using highrise loader
const PageLoader: React.FC = () => <Loader loadingText="Loading page..." />;

const getCachedUser = (): UserDetails | null => {
  try {
    const cached = localStorage.getItem(STORAGE_KEYS.USER);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

// Keep navigation mounted while switching between the primary destinations.
// It is intentionally outside <Routes>, so changing pages does not unmount it.
const PersistentFooter: React.FC = () => {
  const location = useLocation();
  const [user, setUser] = useState<UserDetails | null>(getCachedUser);
  const pathname = location.pathname;
  const isPrimaryDestination = pathname === ROUTER_CONSTANT.ROOT.HOME
    || pathname === ROUTER_CONSTANT.ROOT.ALL_MENU
    || pathname === ROUTER_CONSTANT.PROFILE.ROOT
    || pathname.startsWith(`${ROUTER_CONSTANT.PROFILE.ROOT}/`)
    || pathname === ROUTER_CONSTANT.AGENT.ROOT
    || pathname === ROUTER_CONSTANT.GUARD.SCAN
    // Directory children are task/detail screens. They own their own controls
    // (including floating add buttons), so the main tab bar must not overlap them.
    || pathname === ROUTER_CONSTANT.DIRECTORY.ROOT;

  useEffect(() => {
    const syncUser = (event: Event) => {
      const detail = (event as CustomEvent<UserDetails>).detail;
      setUser(detail || getCachedUser());
    };
    window.addEventListener('user-cache-updated', syncUser);
    return () => window.removeEventListener('user-cache-updated', syncUser);
  }, []);

  if (!isPrimaryDestination || !user || user.status === 'PENDING') return null;
  return <Footer {...user} />;
};

const LocalRoutes: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Warm every page chunk one at a time once the app has settled. Skipped on data-saver.
  useEffect(() => {
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    if (conn?.saveData) return;
    let cancelled = false;
    let i = 0;
    const next = () => {
      if (cancelled || i >= pagePreloaders.length) return;
      pagePreloaders[i++]().catch(() => undefined).finally(() => { setTimeout(next, 120); });
    };
    const t = setTimeout(next, 2500);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  // Deep-link landing guard. A notification deeplink calls navigate(path), but
  // on a cold start the app's bootstrap can redirect to /home immediately after,
  // clobbering it — so member-request / onboard / etc. never opened. We persist
  // the intended path (see useGlobalNativeMessage) and re-apply it ONCE here if
  // we end up somewhere else. "applied" caps it to a single retry so a page that
  // legitimately redirects can't cause a loop.
  useEffect(() => {
    let raw: string | null = null;
    try { raw = sessionStorage.getItem(STORAGE_KEYS.PENDING_DEEP_LINK); } catch { return; }
    if (!raw) return;
    let pending: { path: string; applied: boolean };
    try { pending = JSON.parse(raw); } catch { try { sessionStorage.removeItem(STORAGE_KEYS.PENDING_DEEP_LINK); } catch {} return; }

    if (location.pathname === pending.path) {
      // Arrived at the intended destination — done.
      try { sessionStorage.removeItem(STORAGE_KEYS.PENDING_DEEP_LINK); } catch {}
      return;
    }
    if (pending.applied) {
      // Already retried once and still not there — give up (avoid loops).
      try { sessionStorage.removeItem(STORAGE_KEYS.PENDING_DEEP_LINK); } catch {}
      return;
    }
    // We were clobbered to another route — re-apply the deeplink once.
    pending.applied = true;
    try { sessionStorage.setItem(STORAGE_KEYS.PENDING_DEEP_LINK, JSON.stringify(pending)); } catch {}
    navigate(pending.path, { replace: true });
  }, [location.pathname, navigate]);

  useEffect(() => {
    const PAGE_TITLES: Record<string, string> = {
      '/home': 'Home', '/login': 'Login', '/onboarding': 'Onboarding',
      '/visitor': 'Visitor Management', '/scan': 'QR Scanner',
      '/approval': 'Visitor Approvals', '/profile': 'Profile',
      '/complaint': 'Complaints', '/function': 'Functions',
      '/election': 'Elections', '/maintenance': 'Maintenance',
      '/fund': 'Fund Management', '/notification': 'Notifications',
      '/member': 'Members', '/noticeBoard': 'Notice Board',
      '/polling': 'Polling', '/chat': 'Chat', '/gallary': 'Gallery',
      '/societyDetails': 'Society Details',
      '/manage-guard': 'Manage Guards', '/member-request': 'Member Requests',
      '/analytics': 'Analytics',
      '/vehicles': 'Vehicles & Parking',
      '/directory': 'Directory',
      '/directory/neighbours': 'Directory - Neighbours',
      '/directory/vehicles': 'Directory - Vehicles',
      '/directory/pets': 'Directory - Pets',
      '/directory/guards': 'Directory - Guards',
      '/directory/committee': 'Directory - Committee',
      '/directory/daily-help': 'Directory - Daily Help',
      '/allMenu': 'All Features',
      '/mystatement': 'My Statement', '/sstatement': 'Society Statement',
      '/address': 'My Address', '/userQR': 'My QR',
      '/amenities': 'Amenities',
      '/amenities/bookings/mine': 'My Amenity Bookings',
      '/amenities/requests': 'Amenity Booking Requests',
    };
    const title = PAGE_TITLES[location.pathname] ?? location.pathname;
    trackPageView(location.pathname, title);
  }, [location.pathname]);

  return (
    <>
    <RouteTransition>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path={ROUTER_CONSTANT.ROOT.ROOT} element={<Login />} />
        <Route path={ROUTER_CONSTANT.ROOT.LOGIN} element={<Login />} />
        {/* Protected routes — redirect to login if no token */}
        <Route path={ROUTER_CONSTANT.ROOT.HOME} element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.NOTIFICATION} element={<ProtectedRoute><Notification /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.PROFILE.ROOT} element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.SOCIETY.MEMBER} element={<ProtectedRoute><MemberPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.PROFILE.MY_STATEMENT} element={<ProtectedRoute><Statement /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.PROFILE.SOCIETY_STATEMENT} element={<ProtectedRoute><SocietyStatement /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.PROFILE.EDIT} element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
        <Route path={`${ROUTER_CONSTANT.PROFILE.ADD_ASSET}/:${ROUTER_PARAMS.ASSET_TYPE}?`} element={<ProtectedRoute><AddAsset /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.CHAT} element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.FUNCTION} element={<ProtectedRoute><Function /></ProtectedRoute>} />
        <Route path={`${ROUTER_CONSTANT.ROOT.FUNCTION_DETAIL}/:${ROUTER_PARAMS.FUNCTION_ID}`} element={<ProtectedRoute><FunctionDetail /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.ALL_MENU} element={<ProtectedRoute><AllMenuPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.COMPLAINT} element={<ProtectedRoute><Complaint /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.GALLARY} element={<ProtectedRoute><Gallary /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.NOTICE_BOARD} element={<ProtectedRoute><NoticeBoard /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.POLLING} element={<ProtectedRoute><Polling /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.VISITOR} element={<ProtectedRoute><Visitor /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.MEMBER} element={<ProtectedRoute><MemberPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.USER_QR} element={<ProtectedRoute><UserQR /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.ADDRESS} element={<ProtectedRoute><UserAddress /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.ELECTION} element={<ProtectedRoute><Election /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.ELECTION_DETAIL} element={<ProtectedRoute><ElectionDetails /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.SOCIETY_DOX} element={<ProtectedRoute><SocietyDox /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.SOCIETY.SOCIETY_DETAIL} element={<ProtectedRoute><SocietyDetail /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.SOCIETY.ADD_FUNCTION} element={<ProtectedRoute><AddFunction /></ProtectedRoute>} />
        <Route path={`${ROUTER_CONSTANT.SOCIETY.ADD_FUNCTION}/:${ROUTER_PARAMS.FUNCTION_ID}`} element={<ProtectedRoute><AddFunction /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.SOCIETY.ADMIN + "/" + PAGE_CONSTANT.SOCIETY_ADMIN.PATH_PARAMS.EN} element={<AdminRoute><AdminEmergencyNumber /></AdminRoute>} />
        <Route path={ROUTER_CONSTANT.ROOT.ONBOARDING} element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.SOCIETY.MEMBER_REQUEST} element={<AdminRoute allow={['SOCIETY_ADMIN', 'RESIDENTIAL']}><MemberRequest /></AdminRoute>} />
        <Route path={ROUTER_CONSTANT.SOCIETY.SETTINGS} element={<AdminRoute><SocietySettingsPage /></AdminRoute>} />
        <Route path={ROUTER_CONSTANT.SOCIETY.SUBSCRIPTION} element={<AdminRoute><SubscriptionPage /></AdminRoute>} />
        <Route path={ROUTER_CONSTANT.SOCIETY.ANALYTICS} element={<AdminRoute><AnalyticsDashboard /></AdminRoute>} />
        <Route path={ROUTER_CONSTANT.AGENT.ROOT} element={<ProtectedRoute><AgentPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.AGENT.SUBSCRIPTIONS} element={<AdminRoute allow={['AGENT']}><AgentSubscriptionPage /></AdminRoute>} />
        <Route path={ROUTER_CONSTANT.GUARD.MANAGE} element={<AdminRoute><ManageGuard /></AdminRoute>} />
        <Route path={ROUTER_CONSTANT.APPROVAL.ROOT} element={<ProtectedRoute><ApprovalPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.MAINTENANCE.USER} element={<ProtectedRoute><FeatureRoute feature="FUND_MAINTENANCE"><UserMaintenancePage /></FeatureRoute></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.MAINTENANCE.ADMIN} element={<ProtectedRoute><FeatureRoute feature="FUND_MAINTENANCE"><AdminMaintenancePage /></FeatureRoute></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.MAINTENANCE.ADMIN_VERIFICATIONS} element={<ProtectedRoute><FeatureRoute feature="FUND_MAINTENANCE"><PaymentVerificationsPage /></FeatureRoute></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.MAINTENANCE.ADMIN_STATEMENT_CHECKER} element={<ProtectedRoute><FeatureRoute feature="FUND_MAINTENANCE"><StatementCheckerPage /></FeatureRoute></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.MAINTENANCE.ADMIN_STATEMENT_UPLOAD} element={<ProtectedRoute><FeatureRoute feature="FUND_MAINTENANCE"><StatementUploadPage /></FeatureRoute></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.MAINTENANCE.ADMIN_STATEMENT_HISTORY} element={<ProtectedRoute><FeatureRoute feature="FUND_MAINTENANCE"><StatementHistoryPage /></FeatureRoute></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.GUARD.SCAN} element={<ProtectedRoute><QRScannerPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.GUARD.SOS_HISTORY} element={<ProtectedRoute><SosHistoryPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.VEHICLES} element={<ProtectedRoute><VehiclesPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.FUND.ADMIN} element={<ProtectedRoute><FeatureRoute feature="FUND_MAINTENANCE"><FundPage /></FeatureRoute></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.FUND.USER} element={<ProtectedRoute><FeatureRoute feature="FUND_MAINTENANCE"><FundPage /></FeatureRoute></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.DIRECTORY.ROOT} element={<ProtectedRoute><DirectoryPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.DIRECTORY.NEIGHBOURS} element={<ProtectedRoute><DirectoryNeighboursPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.DIRECTORY.VEHICLES} element={<ProtectedRoute><DirectoryVehiclesPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.DIRECTORY.PETS} element={<ProtectedRoute><DirectoryPetsPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.DIRECTORY.GUARDS} element={<ProtectedRoute><DirectoryGuardsPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.DIRECTORY.COMMITTEE} element={<ProtectedRoute><CommitteePage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.DIRECTORY.DAILY_HELP} element={<ProtectedRoute><DailyHelpPage /></ProtectedRoute>} />
        <Route path={`${ROUTER_CONSTANT.DIRECTORY.DAILY_HELP_CATEGORY_ROOT}/:${ROUTER_PARAMS.DAILY_HELP_CATEGORY}`} element={<ProtectedRoute><DailyHelpCategoryPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.DIRECTORY.DAILY_HELP_MY_FLATS} element={<ProtectedRoute><DailyHelpMyFlatsPage /></ProtectedRoute>} />
        <Route path={`${ROUTER_CONSTANT.DIRECTORY.DAILY_HELP}/:${ROUTER_PARAMS.DAILY_HELP_ID}`} element={<ProtectedRoute><DailyHelpProfilePage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.AMENITIES.ROOT}
            element={<ProtectedRoute><AmenitiesPage /></ProtectedRoute>} />
        {/* Static booking routes must stay above /amenities/:id */}
        <Route path={ROUTER_CONSTANT.AMENITIES.MY_BOOKINGS}
            element={<ProtectedRoute><MyAmenityBookingsPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.AMENITIES.REQUESTS}
            element={<AdminRoute><AmenityBookingRequestsPage /></AdminRoute>} />
        <Route path={ROUTER_CONSTANT.AMENITIES.DETAIL}
            element={<ProtectedRoute><AmenityDetailPage /></ProtectedRoute>} />
        <Route path={ROUTER_CONSTANT.AMENITIES.ADMIN}
            element={<Navigate to={ROUTER_CONSTANT.AMENITIES.REQUESTS} replace />} />
        <Route path="*" element={<Navigate to={localStorage.getItem(STORAGE_KEYS.TOKEN) ? ROUTER_CONSTANT.ROOT.HOME : ROUTER_CONSTANT.ROOT.LOGIN} replace />} />
      </Routes>
    </Suspense>
    </RouteTransition>
    {/* Outside the Suspense on purpose: while a lazy page chunk is still loading, React hides
        everything inside the boundary, which used to blank the bottom nav until it arrived.
        Outside RouteTransition too, so the nav stays put while the page slides. */}
    <PersistentFooter />
    </>
  );
}

export default LocalRoutes;
